import { Button, Form, Modal, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import { useFormik } from 'formik'
import { useEffect } from 'react'
import { useMutation } from '@apollo/client'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import { CREATE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { ICreateResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'
import { flattenCategoryTree } from '../categoryTreeHelpers'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
})

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  categories: IResourceCategory[]
  // Carpeta padre preseleccionada (al crear subcarpeta desde un nodo del árbol)
  parentId?: string | null
}

export const ModalAddCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  categories,
  parentId = null,
}) => {
  const [createCategory, { data, loading, error }] =
    useMutation<ICreateResourceCategoryResponse>(CREATE_RESOURCE_CATEGORY)

  const formik = useFormik({
    initialValues: {
      name: '',
      parentId: parentId || '',
      order: 0,
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (v) => {
      try {
        await createCategory({
          variables: {
            categoryData: {
              name: v.name,
              parentId: v.parentId ? parseId(v.parentId) : null,
              order: v.order,
            },
          },
        })
      } catch (err) {
        console.error('Error creating category:', err)
        toast.error('Error creating folder')
      }
    },
  })

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.createResourceCategory.success) {
      onClose()
      refetch()
      toast.success('Folder created successfully')
      formik.resetForm()
    } else if (
      data.createResourceCategory.errors &&
      data.createResourceCategory.errors.length > 0
    ) {
      toast.error(`Error: ${data.createResourceCategory.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleClose = () => {
    formik.resetForm()
    onClose()
  }

  const parentOptions = flattenCategoryTree(categories)
  const selectedParent = parentOptions.find((opt) => opt.value === formik.values.parentId) || null

  return (
    <Modal show={isOpen} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-folder-plus" /> New Folder
          {loading && <Spinner animation="border" size="sm" />}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form onSubmit={formik.handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">
              Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              name="name"
              placeholder="Folder name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={Boolean(formik.errors.name && formik.touched.name)}
              autoFocus
            />
            {formik.errors.name && formik.touched.name && (
              <Form.Text className="text-danger">{formik.errors.name}</Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Parent folder</Form.Label>
            <Select
              isClearable
              value={selectedParent}
              onChange={(option) => formik.setFieldValue('parentId', option?.value || '')}
              options={parentOptions}
              placeholder="None (root folder)"
            />
            <Form.Text className="text-muted">
              Leave empty to create a root-level folder
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Display Order</Form.Label>
            <Form.Control
              type="number"
              name="order"
              value={formik.values.order}
              onChange={formik.handleChange}
              min="0"
            />
            <Form.Text className="text-muted">Lower numbers appear first</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => formik.handleSubmit()} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Creating...
            </>
          ) : (
            <>
              <i className="bi bi-check-lg me-1" /> Create Folder
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
